import { useEffect, useState } from "react";
import { portalApi, INR, Page, StatCard, usePortal } from "./PortalShell";
import { Users, TrendingUp, Clock, CheckCircle2, X, Gift, Target, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STATE_STYLES = {
  paid: { label: "Paid", cls: "text-emerald-400 bg-emerald-500/10", icon: CheckCircle2 },
  queued: { label: "Queued", cls: "text-sky-400 bg-sky-500/10", icon: Clock },
  pending: { label: "Pending", cls: "text-amber-400 bg-amber-500/10", icon: Clock },
  voided: { label: "Voided", cls: "text-neutral-500 bg-neutral-800", icon: X },
};

// Normalise a raw phone into a wa.me-friendly digit string (drops '+', spaces, dashes).
// Assumes Indian numbers when no country code is present.
function toWhatsappNumber(raw) {
  if (!raw) return null;
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length === 10) return `91${digits}`; // Indian default
  return digits;
}

export default function PortalReferralsPage() {
  const { profile } = usePortal();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    portalApi.get("/affiliate/referrals").then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Load failed"));
  }, []);

  if (err) return <Page title="Referrals"><div className="text-red-400">{err}</div></Page>;
  if (!data) return <Page title="Referrals"><div className="text-neutral-500 italic">Loading…</div></Page>;

  const totalEarned = data.rewards.filter((r) => r.ui_state === "paid").reduce((s, r) => s + r.reward_mrp, 0);
  const totalQueued = data.rewards.filter((r) => r.ui_state === "queued" || r.ui_state === "pending").reduce((s, r) => s + r.reward_mrp, 0);

  const rewardMrp = data.program.reward_mrp;
  const asmName = profile?.asm_name;
  const asmWa = toWhatsappNumber(profile?.asm_phone);

  const referCta = () => {
    const msg = `Hey${asmName ? ` ${asmName}` : ""}, I'd like to refer someone to the Fuelled affiliate program. Can you help me get them onboarded?`;
    if (asmWa) {
      const url = `https://wa.me/${asmWa}?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank", "noopener");
    } else {
      navigator.clipboard.writeText(msg).then(() => toast.success("Message copied — DM your ASM"));
    }
  };

  return (
    <Page title="Referrals" subtitle={`Refer an affiliate → they hit Pro → you earn ₹${rewardMrp.toLocaleString("en-IN")} MRP`} testId="portal-referrals-page">
      {/* How the program works */}
      <div data-testid="section-program-explainer" className="rounded-2xl bg-gradient-to-br from-orange-950/40 via-orange-900/20 to-neutral-950 border border-orange-800/40 p-5 lg:p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="text-xs font-semibold uppercase tracking-widest text-orange-300 flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> Referral Program
            </div>
            <div className="text-lg lg:text-xl font-semibold mt-2 leading-tight">
              Bring a fitness coach into Fuelled. When they hit Pro, you unlock ₹{rewardMrp.toLocaleString("en-IN")} MRP of free stack.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <div className="rounded-xl bg-neutral-950/60 border border-neutral-800 p-3">
                <div className="text-[10px] uppercase tracking-widest text-orange-300 flex items-center gap-1.5">
                  <Gift className="h-3 w-3" /> What you unlock
                </div>
                <div className="text-sm mt-2 text-neutral-200">
                  <b>₹{rewardMrp.toLocaleString("en-IN")} MRP</b> in free Fuelled products for every successful referral that reaches Pro.
                </div>
              </div>
              <div className="rounded-xl bg-neutral-950/60 border border-neutral-800 p-3">
                <div className="text-[10px] uppercase tracking-widest text-orange-300 flex items-center gap-1.5">
                  <Target className="h-3 w-3" /> How they qualify
                </div>
                <ul className="text-xs mt-2 text-neutral-300 space-y-1 list-disc pl-4">
                  <li>They join through you and complete their onboarding</li>
                  <li>They reach the <b>Pro</b> tier (4 lifetime new customers)</li>
                  <li>Max <b>{data.program.annual_cap} referrals</b> per calendar year ({data.program.slots_left} slots left this year)</li>
                </ul>
              </div>
            </div>
          </div>
          <button
            onClick={referCta}
            data-testid="refer-someone-cta"
            className="rounded-full bg-orange-500 hover:bg-orange-400 text-white text-sm font-semibold px-5 py-3 flex items-center gap-2 shrink-0 shadow-[0_10px_30px_-10px_rgba(249,115,22,0.6)]"
          >
            <MessageCircle className="h-4 w-4" />
            {asmWa ? `Refer someone via ${asmName || "your ASM"}` : "Refer someone"}
          </button>
        </div>
      </div>
      {/* Program stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Total Referrals" value={data.rewards.length + data.pipeline.length} testId="ref-stat-total" />
        <StatCard icon={CheckCircle2} label={`Paid (${data.program.year})`} value={INR(totalEarned)} testId="ref-stat-paid" />
        <StatCard icon={Clock} label="Queued + Pending" value={INR(totalQueued)} testId="ref-stat-queued" />
        <StatCard icon={TrendingUp} label="Slots left" value={`${data.program.slots_left} / ${data.program.annual_cap}`} sub={`Annual cap: ${data.program.year}`} testId="ref-stat-slots" />
      </div>

      {/* Earned rewards */}
      <div data-testid="section-rewards-list">
        <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-3">Rewards Earned</div>
        {data.rewards.length === 0 ? (
          <div className="rounded-2xl bg-neutral-900 border border-neutral-800 p-6 text-center text-neutral-500 text-sm">
            No referral rewards yet. Bring another fitness coach into the Fuelled family — when they hit Pro, you earn ₹{data.program.reward_mrp.toLocaleString("en-IN")} MRP.
          </div>
        ) : (
          <div className="space-y-2">
            {data.rewards.map((r, i) => {
              const st = STATE_STYLES[r.ui_state] || STATE_STYLES.pending;
              const Icon = st.icon;
              return (
                <div key={i} className="flex items-center justify-between rounded-2xl bg-neutral-900 border border-neutral-800 px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{r.referred_affiliate_name}</div>
                    <div className="text-[10px] text-neutral-500 mt-0.5">
                      Reached Pro {r.reached_pro_at ? new Date(r.reached_pro_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"} · Currently {r.referred_affiliate_tier || "—"}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-base font-semibold text-orange-400">₹{r.reward_mrp.toLocaleString("en-IN")}</div>
                    <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 mt-1 ${st.cls}`}>
                      <Icon className="h-2.5 w-2.5" /> {st.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pipeline */}
      <div data-testid="section-pipeline">
        <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-3">Pipeline (not yet Pro)</div>
        {data.pipeline.length === 0 ? (
          <div className="rounded-2xl bg-neutral-900 border border-neutral-800 p-6 text-center text-neutral-500 text-sm italic">
            No affiliates you&apos;ve referred are still working toward Pro.
          </div>
        ) : (
          <div className="space-y-2">
            {data.pipeline.map((p, i) => (
              <div key={i} className="flex items-center justify-between rounded-2xl bg-neutral-900 border border-neutral-800 px-4 py-3">
                <div>
                  <div className="text-sm font-medium">{p.name}</div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">Currently {p.current_tier} · {p.lifetime_new_customers} lifetime new customers</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Working toward Pro</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Page>
  );
}
